/**
 * TTJ Indian number grouping for rupee amounts.
 *
 * Shopify's `money` filters (and the theme's section-rendering updates) output international grouping
 * (₹522,623). This script regroups rupee amounts the Indian way (₹5,22,623) without touching prices,
 * the money filters or the store's currency settings: it only rewrites the *display text*.
 *
 *   522623   -> 5,22,623
 *   125000   -> 1,25,000
 *   1000000  -> 10,00,000
 *   ₹1,999   -> ₹1,999   (already correct: unchanged)
 *   ₹5,22,623 -> ₹5,22,623 (idempotent: never double-formats)
 *
 * Only numbers directly attached to a rupee marker (₹, Rs / Rs., INR before the number, or INR right after it)
 * are changed, so quantities, dates, SKUs and review counts are never touched. Decimals are preserved.
 * Dynamically inserted content (variant changes, cart drawer / AJAX cart updates, quick add, facets, search,
 * wishlist cards) is handled by a MutationObserver that runs before the browser paints.
 *
 * To exclude an element, add the attribute data-ttj-keep-money-format to it (or any ancestor).
 */
(function (root) {
  'use strict';

  if (root.TTJIndianMoney) return;

  // Number token: digits with comma-separated digit groups and an optional decimal part.
  // Marker before the number (₹ / Rs / Rs. / INR) OR the number followed by INR.
  var AMOUNT = /((?:₹|\bRs\.?|\bINR)\s*)(\d+(?:,\d+)*(?:\.\d+)?)|(\d+(?:,\d+)*(?:\.\d+)?)(\s*INR\b)/g;
  var MARKER_HINT = /₹|Rs|INR/;

  // 522623 -> 5,22,623 (last three digits, then groups of two).
  function groupDigits(digits) {
    if (digits.length <= 3) return digits;
    var lastThree = digits.slice(-3);
    var rest = digits.slice(0, -3);
    return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
  }

  // "522,623.50" -> "5,22,623.50"; commas are ignored on input, decimals are kept as they are.
  function formatNumber(raw) {
    var match = /^(\d+(?:,\d+)*)(\.\d+)?$/.exec(raw);
    if (!match) return raw;
    return groupDigits(match[1].replace(/,/g, '')) + (match[2] || '');
  }

  // Regroup every rupee amount found inside a string.
  function formatText(text) {
    if (!text || !MARKER_HINT.test(text)) return text;
    return text.replace(AMOUNT, function (whole, marker, amountAfterMarker, amountBeforeInr, inrSuffix) {
      if (amountAfterMarker !== undefined) return marker + formatNumber(amountAfterMarker);
      return formatNumber(amountBeforeInr) + inrSuffix;
    });
  }

  root.TTJIndianMoney = { formatNumber: formatNumber, formatText: formatText };

  if (typeof document === 'undefined') return;

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, NOSCRIPT: 1, TEMPLATE: 1, TITLE: 1 };
  var ATTRIBUTES = ['aria-label', 'aria-valuetext', 'title'];
  var ATTRIBUTE_SELECTOR = '[aria-label],[aria-valuetext],[title]';
  var OPT_OUT = '[data-ttj-keep-money-format]';

  function isExcluded(element) {
    if (!element || element.nodeType !== 1) return false;
    if (SKIP_TAGS[element.nodeName]) return true;
    return !!(element.closest && element.closest(OPT_OUT));
  }

  function fixTextNode(node) {
    var value = node.nodeValue;
    if (!value || !MARKER_HINT.test(value)) return;
    if (isExcluded(node.parentNode)) return;
    var updated = formatText(value);
    if (updated !== value) node.nodeValue = updated;
  }

  function fixAttribute(element, name) {
    var value = element.getAttribute(name);
    if (!value || !MARKER_HINT.test(value) || isExcluded(element)) return;
    var updated = formatText(value);
    if (updated !== value) element.setAttribute(name, updated);
  }

  function fixElementAttributes(element) {
    for (var i = 0; i < ATTRIBUTES.length; i++) {
      if (element.hasAttribute(ATTRIBUTES[i])) fixAttribute(element, ATTRIBUTES[i]);
    }
  }

  function processNode(node) {
    if (!node) return;

    if (node.nodeType === 3) {
      fixTextNode(node);
      return;
    }
    if (node.nodeType !== 1 && node.nodeType !== 11) return;
    if (node.nodeType === 1 && isExcluded(node)) return;

    var walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode: function (candidate) {
        if (candidate.nodeType === 1) {
          // Rejecting an element skips its whole subtree (scripts, styles, opted-out blocks).
          return isExcluded(candidate) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    while (walker.nextNode()) fixTextNode(walker.currentNode);

    if (node.nodeType === 1) fixElementAttributes(node);
    if (node.querySelectorAll) {
      var withAttributes = node.querySelectorAll(ATTRIBUTE_SELECTOR);
      for (var i = 0; i < withAttributes.length; i++) fixElementAttributes(withAttributes[i]);
    }
  }

  function start() {
    processNode(document.body);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type === 'childList') {
          for (var j = 0; j < mutation.addedNodes.length; j++) processNode(mutation.addedNodes[j]);
        } else if (mutation.type === 'characterData') {
          fixTextNode(mutation.target);
        } else if (mutation.type === 'attributes') {
          fixAttribute(mutation.target, mutation.attributeName);
        }
      }
      // Drop the records produced by our own edits (idempotent anyway, this just avoids extra passes).
      observer.takeRecords();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRIBUTES
    });
  }

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  }
})(typeof window !== 'undefined' ? window : globalThis);
